import React, { useState, useRef, useEffect } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';

interface User {
    id: string;
    name: string;
}

interface ChatUserSelectorProps {
    users: User[];
    selectedUserId: string | null;
    onUserChange: (userId: string | null) => void;
}

export const ChatUserSelector: React.FC<ChatUserSelectorProps> = ({
    users,
    selectedUserId,
    onUserChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedUser = users.find(u => u.id === selectedUserId);
    const availableUsers = users.filter(u => u.id !== selectedUserId);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelectUser = (userId: string) => {
        onUserChange(userId);
        setIsOpen(false);
    };

    const handleRemoveUser = () => {
        onUserChange(null);
    };

    return (
        <div className="space-y-2">
            {selectedUser && (
                <div className="flex items-center gap-2">
                    <div className="chip active">
                        <span className="max-w-[140px] truncate">{selectedUser.name}</span>
                        <button
                            type="button"
                            onClick={handleRemoveUser}
                            className="chip-remove"
                            title="Remover responsável"
                            aria-label="Remover responsável"
                        >
                            <AppIcon name="x" className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            )}

            {!selectedUser && (
                <div className="relative" ref={dropdownRef}>
                    <button
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        className="chip chip--dashed min-h-11 touch-manipulation pointer-fine:min-h-7"
                        aria-expanded={isOpen}
                        aria-haspopup="listbox"
                    >
                        <AppIcon name="user" className="h-3.5 w-3.5" aria-hidden="true" />
                        <span>Atribuir responsável</span>
                    </button>

                    {isOpen && (
                        <div className="absolute left-0 z-dropdown mt-2 w-56 rounded-lg border border-border bg-surface shadow-lg">
                            <div className="max-h-48 overflow-y-auto p-2">
                                {availableUsers.length === 0 ? (
                                    <p className="px-2 py-4 text-center text-caption text-muted">
                                        Nenhum usuário disponível
                                    </p>
                                ) : (
                                    availableUsers.map(user => (
                                        <button
                                            key={user.id}
                                            type="button"
                                            onClick={() => handleSelectUser(user.id)}
                                            className="flex min-h-11 w-full items-center gap-2 rounded-md px-3 py-2 text-left transition-colors hover:bg-surface-subtle"
                                        >
                                            <div className="deal-avatar flex-shrink-0">
                                                {user.name.substring(0, 2).toUpperCase()}
                                            </div>
                                            <span className="truncate text-body-sm text-secondary">{user.name}</span>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
